import React, { Component, ComponentType, PropsWithChildren } from "react";
import { ErrorFallback, ErrorFallbackProps } from "@/components/ErrorFallback";
import { reportCaughtError } from "@/sentry";

export type ErrorBoundaryProps = PropsWithChildren<{
  FallbackComponent?: ComponentType<ErrorFallbackProps>;
  onError?: (error: Error, stackTrace: string) => void;
}>;

type ErrorBoundaryState = { error: Error | null };

/**
 * This is a special case for for using the class components. Error boundaries must be class components because React only provides error boundary functionality through lifecycle methods (componentDidCatch and getDerivedStateFromError) which are not available in functional components.
 * https://react.dev/reference/react/Component#catching-rendering-errors-with-an-error-boundary
 */

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { error: null };

  static defaultProps: {
    FallbackComponent: ComponentType<ErrorFallbackProps>;
  } = {
    FallbackComponent: ErrorFallback,
  };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: { componentStack: string }): void {
    // ── This used to report NOTHING ──────────────────────────────────────────
    //
    // It built a full `errorLog` (message, stack, componentStack, timestamp)
    // and then emitted none of it: the three `console.error` calls had been
    // stripped to `/* production: no log */`, and its only other sink was
    // `localStorage` — which DOES NOT EXIST in React Native, so the `try/catch`
    // swallowed a ReferenceError and persisted nothing. On Android a caught
    // React error produced zero diagnostics anywhere, which is half of why the
    // Broken Functionality rejection was undiagnosable.
    //
    // It now reports through the SAME sink as the global handler, so the two
    // cannot drift: to logcat always (Play Console attaches logcat to a crash
    // report, so this is what Google actually sees), and to Sentry when a DSN
    // exists.
    reportCaughtError(error, {
      fatal: false, // the boundary caught it — the app renders a fallback
      origin: "react-render",
      componentStack: info.componentStack,
    });

    if (typeof this.props.onError === "function") {
      this.props.onError(error, info.componentStack);
    }
  }

  resetError = (): void => {
    this.setState({ error: null });
  };

  render() {
    const { FallbackComponent } = this.props;

    return this.state.error && FallbackComponent ? (
      <FallbackComponent
        error={this.state.error}
        resetError={this.resetError}
      />
    ) : (
      this.props.children
    );
  }
}
